import type { WasserijItemPriceBreakdown } from '../../lib/wasserij-items';
import { formatCurrency } from '../../lib/format';

interface Props {
  breakdown: WasserijItemPriceBreakdown;
  areaM2: number | null;
  productName: string;
  /** Live extra-regels (vinkjes); null = gebruik de server-breakdown. */
  previewExtras?: WasserijItemPriceBreakdown['extras'] | null;
}

export function PriceBreakdown({ breakdown, areaM2, productName, previewExtras = null }: Props) {
  const cleaningLabel = areaM2
    ? `Reiniging ${productName} (${areaM2.toFixed(2)} m²)`
    : `Reiniging ${productName}`;

  const extras = previewExtras ?? breakdown.extras;
  const extrasTotal = extras.reduce((sum, e) => sum + e.price, 0);
  const total =
    previewExtras == null
      ? breakdown.total
      : Math.round((breakdown.cleaning + (breakdown.package?.price ?? 0) + extrasTotal) * 100) / 100;

  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
      <h3 className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-3">
        Prijsoverzicht
      </h3>
      <div className="space-y-1.5 text-sm">
        <Row label={cleaningLabel} value={formatCurrency(breakdown.cleaning)} />
        {breakdown.package && (
          <Row
            label={`Pakket: ${breakdown.package.name}`}
            value={formatCurrency(breakdown.package.price)}
          />
        )}
        {extras.map((e) => (
          <Row key={e.id} label={`Extra: ${e.name}`} value={formatCurrency(e.price)} />
        ))}
        <div className="border-t border-slate-200 pt-2 mt-2">
          <div className="flex items-baseline justify-between font-semibold text-slate-900">
            <span>Totaal incl. BTW</span>
            <span className="font-mono">{formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between text-slate-700">
      <span className="pr-3">{label}</span>
      <span className="font-mono whitespace-nowrap">{value}</span>
    </div>
  );
}
